import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { GoogleLogo, ArrowsClockwise, Sparkle, Warning } from "@phosphor-icons/react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { fmtNum, fmtCurrency } from "./format";
import { SkeletonCard } from "./Skeleton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLOR = {
  ENABLED: { bg: "#16A34A22", color: "#86EFAC", label: "Enabled" },
  PAUSED: { bg: "#71717A33", color: "#D4D4D8", label: "Paused" },
  REMOVED: { bg: "#FF444422", color: "#FF6666", label: "Removed" },
  UNKNOWN: { bg: "#3F3F4633", color: "#A1A1AA", label: "Unknown" },
};

const KpiCard = ({ label, value, sub }) => (
  <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid={`gads-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <div className="text-[10px] uppercase tracking-wider text-white/45">{label}</div>
    <div className="text-lg font-semibold text-white mt-1" style={{ fontFamily: "Varela Round, sans-serif" }}>{value}</div>
    {sub && <div className="text-[10px] text-white/50 mt-0.5">{sub}</div>}
  </div>
);

const SpendChart = ({ data, currency }) => {
  if (!data || data.length === 0) {
    return <div className="text-xs text-white/40 text-center py-8">No daily data in this window.</div>;
  }
  return (
    <div className="h-56" data-testid="gads-spend-chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="gadsSpendFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#4285F4" stopOpacity={0.5} />
              <stop offset="100%" stopColor="#4285F4" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gadsConvFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#34A853" stopOpacity={0.5} />
              <stop offset="100%" stopColor="#34A853" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#ffffff10" vertical={false} />
          <XAxis dataKey="date" tick={{ fill: "#ffffff60", fontSize: 10 }} axisLine={{ stroke: "#ffffff20" }} tickLine={false} />
          <YAxis yAxisId="left" tick={{ fill: "#ffffff60", fontSize: 10 }} axisLine={false} tickLine={false}
                 tickFormatter={(v) => fmtNum(v, { compact: true })} />
          <YAxis yAxisId="right" orientation="right" tick={{ fill: "#ffffff60", fontSize: 10 }} axisLine={false} tickLine={false}
                 tickFormatter={(v) => fmtNum(v, { compact: true })} />
          <Tooltip
            contentStyle={{ background: "#0B0B0F", border: "1px solid #ffffff20", borderRadius: 6, fontSize: 11 }}
            labelStyle={{ color: "#ffffff80" }}
            formatter={(value, name) => {
              if (name === "Spend") return [fmtCurrency(value, currency), name];
              return [fmtNum(value), name];
            }}
          />
          <Area yAxisId="left" type="monotone" dataKey="spend" name="Spend" stroke="#4285F4" strokeWidth={2} fill="url(#gadsSpendFill)" />
          <Area yAxisId="right" type="monotone" dataKey="conversions" name="Conversions" stroke="#34A853" strokeWidth={2} fill="url(#gadsConvFill)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const CampaignsTable = ({ rows, currency }) => {
  if (!rows || rows.length === 0) {
    return <div className="text-xs text-white/40 text-center py-8">No campaigns ran in this window.</div>;
  }
  return (
    <div className="overflow-x-auto rounded border border-white/10" data-testid="gads-campaigns-table">
      <table className="w-full text-xs">
        <thead className="bg-white/[0.03] text-white/55 uppercase tracking-wider text-[10px]">
          <tr>
            <th className="text-left px-3 py-2">Campaign</th>
            <th className="text-left px-3 py-2">Status</th>
            <th className="text-right px-3 py-2">Spend</th>
            <th className="text-right px-3 py-2">Impr.</th>
            <th className="text-right px-3 py-2">Clicks</th>
            <th className="text-right px-3 py-2">CTR</th>
            <th className="text-right px-3 py-2">CPC</th>
            <th className="text-right px-3 py-2">Conv.</th>
            <th className="text-right px-3 py-2">ROAS</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 25).map((c) => {
            const st = STATUS_COLOR[c.status] || STATUS_COLOR.UNKNOWN;
            return (
              <tr key={c.campaign_id} className="border-t border-white/5 hover:bg-white/[0.02]">
                <td className="px-3 py-2 text-white/85 truncate max-w-[260px]" title={c.name}>{c.name}</td>
                <td className="px-3 py-2">
                  <span className="px-1.5 py-0.5 rounded text-[10px]" style={{ background: st.bg, color: st.color }}>{st.label}</span>
                </td>
                <td className="px-3 py-2 text-right text-white">{fmtCurrency(c.spend, currency)}</td>
                <td className="px-3 py-2 text-right text-white/80">{fmtNum(c.impressions)}</td>
                <td className="px-3 py-2 text-right text-white/80">{fmtNum(c.clicks)}</td>
                <td className="px-3 py-2 text-right text-white/80">{c.ctr != null ? `${c.ctr}%` : "—"}</td>
                <td className="px-3 py-2 text-right text-white/80">{fmtCurrency(c.cpc, currency)}</td>
                <td className="px-3 py-2 text-right text-white/80">{fmtNum(c.conversions)}</td>
                <td className="px-3 py-2 text-right text-white">{c.roas != null ? `${c.roas}x` : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {rows.length > 25 && (
        <div className="text-[10px] text-white/40 px-3 py-2 bg-white/[0.02]">Showing top 25 of {rows.length} campaigns.</div>
      )}
    </div>
  );
};

const AiInsight = ({ ai }) => {
  if (!ai || !ai.summary) return null;
  return (
    <div className="rounded-lg border border-[#4285F422] bg-gradient-to-r from-[#4285F411] to-transparent p-4" data-testid="gads-ai-insight">
      <div className="flex items-center gap-2 mb-2">
        <Sparkle size={14} className="text-[#FFEB00]" />
        <span className="text-[10px] uppercase tracking-wider text-white/55">AI Performance Brief</span>
      </div>
      <p className="text-sm text-white/85 leading-relaxed">{ai.summary}</p>
      {Array.isArray(ai.recommendations) && ai.recommendations.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {ai.recommendations.slice(0, 5).map((r, i) => (
            <li key={i} className="flex items-start gap-2 text-xs">
              <span
                className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${
                  r.priority === "P0" ? "bg-red-500/20 text-red-300"
                  : r.priority === "P1" ? "bg-yellow-500/20 text-yellow-300"
                  : "bg-white/10 text-white/70"
                }`}
              >
                {r.priority || "P2"}
              </span>
              <div>
                <span className="text-white/90 font-medium">{r.title}</span>
                {r.detail && <span className="text-white/55"> — {r.detail}</span>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const GoogleAdsWidget = ({ clientId, currency = "INR", dateRange }) => {
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [insights, setInsights] = useState(null);
  const [campaigns, setCampaigns] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const sd = dateRange?.start_date;
  const ed = dateRange?.end_date;

  const loadStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const res = await axios.get(`${API}/clients/${clientId}/google-ads/status`);
      setStatus(res.data);
    } catch {
      setStatus(null);
    } finally {
      setLoadingStatus(false);
    }
  }, [clientId]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const loadData = useCallback(async () => {
    if (!sd || !ed) return;
    if (!status?.connected || !status?.account_linked) return;
    setLoading(true); setErr("");
    try {
      const [insRes, campRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}/google-ads/insights`, { params: { start_date: sd, end_date: ed } }),
        axios.get(`${API}/clients/${clientId}/google-ads/campaigns`, { params: { start_date: sd, end_date: ed } }),
      ]);
      setInsights(insRes.data);
      setCampaigns(campRes.data?.campaigns || []);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Failed to load Google Ads data");
      setInsights(null);
      setCampaigns(null);
    } finally {
      setLoading(false);
    }
  }, [clientId, sd, ed, status?.connected, status?.account_linked]);

  useEffect(() => { loadData(); }, [loadData]);

  const kpis = insights?.kpis;
  const trend = insights?.trend;
  const cur = insights?.currency || currency;

  const totalConvValue = useMemo(() => kpis?.conversions_value, [kpis]);

  if (loadingStatus) return <SkeletonCard rows={4} />;

  // Not configured at platform level
  if (status && !status.configured) {
    return (
      <div className="rounded border border-white/10 bg-white/[0.02] p-4 text-xs text-white/60" data-testid="gads-widget-not-configured">
        Google Ads is not configured for this workspace. A super-admin needs to set `GOOGLE_ADS_DEVELOPER_TOKEN` in the backend env.
      </div>
    );
  }

  // Not connected → empty state, point to client edit page
  if (!status?.connected) {
    return (
      <div className="rounded border border-white/10 bg-white/[0.02] p-4 flex items-center justify-between gap-3" data-testid="gads-widget-not-connected">
        <div className="flex items-center gap-3">
          <GoogleLogo size={22} className="text-white/60" />
          <div>
            <div className="text-sm text-white/85 font-medium">Google Ads not connected</div>
            <div className="text-[11px] text-white/50">Connect this client in the Edit Client panel to see paid search performance here.</div>
          </div>
        </div>
      </div>
    );
  }

  // Connected but no account linked
  if (status?.connected && !status?.account_linked) {
    return (
      <div className="rounded border border-yellow-500/30 bg-yellow-500/[0.05] p-4 flex items-center gap-3" data-testid="gads-widget-no-account">
        <Warning size={18} className="text-yellow-300" />
        <div>
          <div className="text-sm text-white/85 font-medium">OAuth connected — but no Ads account is linked yet</div>
          <div className="text-[11px] text-white/55">Go to Edit Client → Google Ads → &quot;Fetch accessible accounts&quot; and pick the right account.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="gads-widget">
      {/* Header strip with account + refresh */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[11px] text-white/55">
          <GoogleLogo size={14} className="text-[#4285F4]" />
          <span>Account: <span className="text-white/85 font-medium">{status?.account_name || status?.customer_id}</span></span>
          {status?.login_customer_id && (
            <span className="text-white/40">· via MCC {status.login_customer_id}</span>
          )}
        </div>
        <button
          onClick={() => loadData()}
          disabled={loading}
          className="flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded border border-white/15 text-white/70 hover:bg-white/[0.04] disabled:opacity-50"
          data-testid="gads-refresh"
        >
          <ArrowsClockwise size={12} /> {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {err && (
        <div className="rounded border border-red-500/30 bg-red-500/[0.05] p-3 text-xs text-red-200" data-testid="gads-error">
          {err}
        </div>
      )}

      {loading && !insights ? (
        <SkeletonCard rows={6} />
      ) : (
        <>
          {/* KPI row */}
          {kpis && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="gads-kpis">
              <KpiCard label="Spend" value={fmtCurrency(kpis.spend, cur)} />
              <KpiCard label="Impressions" value={fmtNum(kpis.impressions, { compact: true })} />
              <KpiCard label="Clicks" value={fmtNum(kpis.clicks, { compact: true })} />
              <KpiCard label="CTR" value={`${kpis.ctr}%`} />
              <KpiCard label="CPC" value={fmtCurrency(kpis.cpc, cur)} />
              <KpiCard label="Conversions" value={fmtNum(kpis.conversions)} />
              <KpiCard label="Conv. Value" value={fmtCurrency(totalConvValue, cur)} />
              <KpiCard label="ROAS" value={kpis.roas != null ? `${kpis.roas}x` : "—"} />
            </div>
          )}

          {/* Trend chart */}
          {trend && trend.length > 0 && (
            <div className="rounded border border-white/10 bg-white/[0.02] p-4">
              <div className="text-[10px] uppercase tracking-wider text-white/50 mb-2">Daily Spend & Conversions</div>
              <SpendChart data={trend} currency={cur} />
            </div>
          )}

          {/* AI insight */}
          <AiInsight ai={insights?.ai} />

          {/* Campaigns table */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/50 mb-2">Campaigns</div>
            <CampaignsTable rows={campaigns} currency={cur} />
          </div>
        </>
      )}
    </div>
  );
};

export default GoogleAdsWidget;
